/**
 * 文件处理工具类
 * 用于读取、处理和导出 Excel/CSV 文件
 */

import * as XLSX from 'xlsx';
import { judgePlatformType } from './platformTypeUtils';

export interface FileData {
  columns: string[];
  rows: Array<Record<string, any>>;
}

export interface ColumnMapping {
  sourceSite?: string | null;
  mediaType?: string | null;
  publisherType?: string | null;
}

export interface ProcessAndExportOptions {
  format?: 'xlsx' | 'csv';
  fileName?: string;
  newColumnName?: string;
}

export interface ProcessAndExportResult {
  success: boolean;
  rowCount: number;
}

/**
 * 读取 Excel 或 CSV 文件
 * @param file - 上传的文件
 * @returns 解析后的数据
 */
export async function readExcelOrCsv(file: File): Promise<FileData> {
  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  
  if (!firstSheetName) {
    return { columns: [], rows: [] };
  }
  
  const sheet = workbook.Sheets[firstSheetName];
  const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' }) as Array<Record<string, any>>;
  
  // 提取列名
  const columnSet = rawRows.reduce<Set<string>>((set, row) => {
    Object.keys(row || {}).forEach(k => set.add(String(k || '').trim()));
    return set;
  }, new Set<string>());
  const columns = Array.from(columnSet).filter(Boolean) as string[];
  
  // 规范化行数据
  const rows = rawRows.map(r => {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(r || {})) {
      out[String(k || '').trim()] = v;
    }
    return out;
  });
  
  return { columns, rows };
}

/**
 * 处理数据并添加平台类型列
 * @param rows - 原始数据行
 * @param columnMapping - 列名映射配置
 * @param newColumnName - 新增的平台类型列名
 * @returns 处理后的数据行
 */
export function processDataWithPlatformType(
  rows: Array<Record<string, any>>,
  columnMapping: ColumnMapping,
  newColumnName: string = '平台类型'
): Array<Record<string, any>> {
  const { sourceSite, mediaType, publisherType } = columnMapping;
  
  return rows.map(row => {
    const newRow = { ...row };
    
    // 使用平台类型判断工具类
    const platformType = judgePlatformType({
      sourceSite: sourceSite ? String(row[sourceSite] || '') : '',
      mediaType: mediaType ? String(row[mediaType] || '') : '',
      publisherType: publisherType ? String(row[publisherType] || '') : ''
    });
    
    // 添加平台类型列
    newRow[newColumnName] = platformType;
    
    return newRow;
  });
}

/**
 * 导出为 Excel 文件
 * @param rows - 数据行
 * @param columns - 列名数组
 * @param fileName - 文件名
 */
export function exportToExcel(
  rows: Array<Record<string, any>>,
  columns: string[],
  fileName: string = 'processed_data.xlsx'
): void {
  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  
  // 准备数据：第一行是列名，后续行是数据
  const data: any[][] = [columns];
  rows.forEach(row => {
    const rowData = columns.map(col => row[col] ?? '');
    data.push(rowData);
  });
  
  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  
  // 设置列宽（可选）
  const colWidths = columns.map(() => ({ wch: 15 }));
  worksheet['!cols'] = colWidths;
  
  // 添加工作表到工作簿
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  
  // 导出文件
  XLSX.writeFile(workbook, fileName);
}

/**
 * 导出为 CSV 文件
 * @param rows - 数据行
 * @param columns - 列名数组
 * @param fileName - 文件名
 */
export function exportToCsv(
  rows: Array<Record<string, any>>,
  columns: string[],
  fileName: string = 'processed_data.csv'
): void {
  // 创建工作表数据
  const data: string[][] = [columns];
  rows.forEach(row => {
    const rowData = columns.map(col => {
      const value = row[col] ?? '';
      // CSV 转义：如果包含逗号、引号或换行符，需要用引号包裹
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    });
    data.push(rowData);
  });
  
  // 转换为 CSV 字符串
  const csvContent = data.map(row => row.join(',')).join('\n');
  
  // 添加 BOM 以支持中文（可选）
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // 创建下载链接
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // 释放 URL 对象
  URL.revokeObjectURL(url);
}

/**
 * 完整的文件处理流程：读取 -> 处理 -> 导出
 * @param file - 上传的文件
 * @param columnMapping - 列名映射配置
 * @param options - 导出选项
 * @returns 处理结果
 */
export async function processAndExportFile(
  file: File,
  columnMapping: ColumnMapping,
  options: ProcessAndExportOptions = {}
): Promise<ProcessAndExportResult> {
  const {
    format = 'xlsx',
    fileName,
    newColumnName = '平台类型'
  } = options;
  
  try {
    // 1. 读取文件
    const { columns, rows } = await readExcelOrCsv(file);
    
    if (rows.length === 0) {
      throw new Error('文件中没有数据');
    }
    
    // 2. 处理数据（添加平台类型列）
    const processedRows = processDataWithPlatformType(rows, columnMapping, newColumnName);
    
    // 3. 准备导出列（包含原有列 + 新增的平台类型列）
    const exportColumns = [...columns];
    if (!exportColumns.includes(newColumnName)) {
      exportColumns.push(newColumnName);
    }
    
    // 4. 生成文件名
    const originalName = file.name.replace(/\.[^/.]+$/, ''); // 去掉扩展名
    const extension = format === 'csv' ? '.csv' : '.xlsx';
    const finalFileName = fileName || `${originalName}_processed${extension}`;
    
    // 5. 导出文件
    if (format === 'csv') {
      exportToCsv(processedRows, exportColumns, finalFileName);
    } else {
      exportToExcel(processedRows, exportColumns, finalFileName);
    }
    
    return { success: true, rowCount: processedRows.length };
  } catch (error) {
    console.error('文件处理失败:', error);
    throw error;
  }
}

